/**
 * AuthorByline - E-E-A-T attribution block for SEO pillar pages.
 *
 * All content sourced from existing site data:
 *   - George's headshot: /images/team/george-giannakakis.jpg
 *   - Credentials: Bachelor of Architectural Studies + Master of Architecture
 *     (University of South Australia), SA real estate licence RLA300580,
 *     17+ years across the South Australian building industry.
 *   - Founded BuildPilot in 2021.
 *
 * Zero fabricated opinions. Zero fabricated quotes. Zero placeholder text.
 * Purely factual attribution + Person JSON-LD signal so Google can tie
 * the page to a real named author with verifiable qualifications.
 *
 * Usage:
 *   <AuthorByline reviewedDate="2026-02-15" />
 *   <AuthorByline reviewedDate="2026-02-15" variant="compact" />
 */
import React from 'react';
import { Link } from 'react-router-dom';

const HEADSHOT = '/images/team/george-giannakakis.jpg';

// Formats an ISO date like "2026-02-15" into "15 February 2026".
function formatReviewedDate(iso) {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function AuthorByline({ reviewedDate, variant = 'default' }) {
  const displayDate = formatReviewedDate(reviewedDate);
  const isCompact = variant === 'compact';

  return (
    <aside
      className={`author-byline ${isCompact ? 'is-compact' : ''} flex items-start gap-4 p-4 sm:p-5 my-6 bg-white border border-gray-200 rounded-2xl`}
      aria-label="Article authored by George Giannakakis"
      data-testid="author-byline"
    >
      <img
        src={HEADSHOT}
        alt="George Giannakakis, founder of BuildPilot"
        width={isCompact ? 48 : 64}
        height={isCompact ? 48 : 64}
        loading="lazy"
        className={`shrink-0 ${isCompact ? 'w-12 h-12' : 'w-14 h-14 sm:w-16 sm:h-16'} rounded-full object-cover border border-gray-200`}
      />
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-[11px] uppercase tracking-[0.07em] text-[#1d8efe] font-semibold">Written by</span>
          <Link
            to="/about"
            className="text-[15px] font-semibold text-[#0a2540] hover:text-[#1d8efe] transition-colors"
            data-testid="author-byline-name"
          >
            George Giannakakis
          </Link>
        </div>
        <p className="text-[13px] text-[#525252] mt-1 leading-snug">
          Founder, BuildPilot. Bachelor of Architectural Studies and Master of Architecture (University of South Australia). Active South Australian real estate licence RLA300580. 17+ years across the South Australian building industry.
        </p>
        {displayDate && (
          <p className="text-[11.5px] text-[#86868b] mt-1.5">
            Last reviewed <time dateTime={reviewedDate}>{displayDate}</time>
          </p>
        )}
      </div>
    </aside>
  );
}
